import { useState, useContext, useEffect } from 'react';
import { Chip } from '@mui/material';
import {
    Euro,
    Close,
    Refresh,
    Done,
    Error as ErrorIcon,
    HourglassEmpty,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AppContext, SttChipUser, SttTable } from '../../../sporttia/all';
import BookingDialog from '../../../components/dialogs/bookingDialog/BookingDialog';
import ModalCollectBatch from './ModalCollectBatch';
import { getBookingScheduleBookingAmounts } from './bookingScheduleBookingAmounts';
import PaymentModal from '../../../components/paymentModal/PaymentModal';
import { SttTopFilteringControls } from '../../../sporttia/SttTopFilteringControls';
import { translatePlaceholders } from '../../../lib/translatePlaceholders';

type Props = {
    bookings: unknown;
    onFetch: (params?: unknown) => Promise<unknown> | void;
};

type Booking = {
    id: string;
    booking?: {
        id?: string;
        price?: number;
        totalPaid?: number;
        totalBill?: number;
        error?: string;
        ini?: string;
        end?: string;
    };
    purse?: {
        fullname?: string;
    };
    facility?: unknown;
    price?: number;
    totalPaid?: number;
    totalBill?: number;
    status?: string;
    ini?: string;
    end?: string;
};

type BookingsData = {
    rows: Booking[];
    count?: number;
};

export default function BookingScheduleBookings({
    bookings,
    onFetch,
}: Props): React.ReactElement {
    const getInitialBookings = (bookingsData: unknown): BookingsData => {
        if (
            !bookingsData ||
            typeof bookingsData !== 'object' ||
            !('rows' in bookingsData)
        ) {
            return { rows: [] };
        }
        const data = bookingsData as { rows?: unknown[] };
        if (!data.rows) return { rows: [] };
        return {
            ...bookingsData,
            rows: data.rows.map((row: unknown) => ({
                ...(row as object),
                id:
                    (row as { booking?: { id?: string } }).booking?.id ||
                    crypto.randomUUID(),
            })),
        } as BookingsData;
    };

    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [viewBooking, setViewBooking] = useState<Booking | null>(null);
    const [_bookings, setBookings] = useState<BookingsData>(() =>
        getInitialBookings(bookings),
    );
    const [batch, setBatch] = useState<Booking[]>([]);
    const [idPayModal, setIdPayModal] = useState<string | null>(null);
    const [filterParams, setFilterParams] = useState<Record<string, unknown>>(
        {},
    );
    const [selectedRows, setSelectedRows] = useState<Booking[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [rowsPerPage] = useState<number>(20);
    const [preset, setPreset] = useState<string>('ALL');

    // Estados que acepta back
    const BOOKING_STATUS = {
        PENDING: 'PENDING',
        PUBLISHED: 'PUBLISHED',
        PROCESSED: 'PROCESSED',
        FAILED: 'FAILED',
    };

    // Helper: fusionar múltiples llamadas para IN_PROGRESS
    const fetchAndMergeStatuses = async (
        statuses: string[],
        filters: Record<string, unknown>,
    ) => {
        const responses = await Promise.all(
            statuses.map((st: any) =>
                onFetch({
                    ...filters,
                    status: st,
                }),
            ),
        );
        // combinar respuestas
        return {
            count: responses.reduce(
                (acc: any, r: any) =>
                    acc + ((r as { count?: number })?.count || 0),
                0,
            ),
            rows: responses.flatMap(
                (r) => (r as { rows?: Booking[] })?.rows || [],
            ),
        };
    };

    // Helper: filtros comunes
    const getCleanFilters = (
        customParams: Record<string, unknown> = {},
    ): Record<string, unknown> => {
        const clean = { ...customParams };
        if (filterParams?.ini !== null) clean.ini = filterParams.ini;
        if (filterParams?.end !== null) clean.end = filterParams.end;
        if (filterParams?.status !== null) clean.status = filterParams.status;
        return clean;
    };

    const handleRefresh = async () => {
        const filters = {
            ...getCleanFilters(),
            page: currentPage,
            rows: rowsPerPage,
        };

        if (preset === 'IN_PROGRESS') {
            const merged = await fetchAndMergeStatuses(
                [BOOKING_STATUS.PENDING, BOOKING_STATUS.PUBLISHED],
                filters,
            );
            setBookings(merged as BookingsData);
        } else if (preset !== 'ALL') {
            onFetch({
                ...filters,
                status: preset,
            });
        } else {
            onFetch(filters);
        }
    };

    // TODO: Extraer a un componente Chip personalizado en lugar de usar estilos inline
    const STATUS_STYLES = {
        IN_PROGRESS: {
            label: 'EN PROCESO',
            color: '#2196f3',
            icon: HourglassEmpty,
            includes: [BOOKING_STATUS.PENDING, BOOKING_STATUS.PUBLISHED],
        },
        SUCCEEDED: {
            label: 'FINALIZADO',
            color: '#4caf50',
            icon: Done,
            includes: [BOOKING_STATUS.PROCESSED],
        },
        ERROR: {
            label: 'ERROR',
            color: '#f44336',
            icon: ErrorIcon,
            includes: [BOOKING_STATUS.FAILED],
        },
    };

    const getStatusConfig = (status: string) => {
        const statusConfig =
            Object.values(STATUS_STYLES).find((config: any) =>
                config.includes?.includes(status),
            ) || STATUS_STYLES.IN_PROGRESS;

        const IconComponent = statusConfig.icon;
        return {
            ...statusConfig,
            icon: <IconComponent style={{ color: 'white', fontSize: 16 }} />,
        };
    };

    const renderStatus = (status: string) => {
        const config = getStatusConfig(status);

        return (
            <Chip
                icon={config.icon}
                size="small"
                label={config.label}
                style={{
                    backgroundColor: config.color,
                    color: 'white',
                    fontWeight: 500,
                }}
            />
        );
    };

    useEffect(() => {
        setBookings(getInitialBookings(bookings));
    }, [bookings]);

    function openCollectBookings(rows: Booking[]) {
        const fullRows =
            _bookings.rows?.filter((row: any) =>
                rows.some(
                    (selectedRow: any) => selectedRow.id === row.booking?.id,
                ),
            ) || [];
        setBatch(fullRows);
    }

    async function doFiltering(params: Record<string, unknown> = {}) {
        const { ini = null, end = null, status } = params;

        // Actualizamos el preset solo si se proporciona un status en los parámetros
        if (status !== undefined) {
            setPreset(status as string);
        }

        setCurrentPage(1);

        const baseFilters: Record<string, unknown> = {
            ...(ini !== undefined && { ini }),
            ...(end !== undefined && { end }),
            page: 1,
            rows: rowsPerPage,
        };

        const statusToUse = status !== undefined ? status : preset;

        if (statusToUse === 'IN_PROGRESS') {
            const merged = await fetchAndMergeStatuses(
                [BOOKING_STATUS.PENDING, BOOKING_STATUS.PUBLISHED],
                baseFilters,
            );
            setFilterParams({ ...baseFilters, status: 'IN_PROGRESS' });
            setBookings(merged as BookingsData);
        } else if (statusToUse && statusToUse !== 'ALL') {
            setFilterParams({ ...baseFilters, status: statusToUse });
            onFetch({ ...baseFilters, status: statusToUse });
        } else {
            setFilterParams(baseFilters);
            onFetch(baseFilters);
        }
    }

    function collectBookings(paymentForm: string) {
        if (batch.length > 0) {
            cxt?.api('POST', '/batch', {
                params: {
                    calls: batch
                        .filter(
                            (booking: Booking) =>
                                booking.booking?.id !== undefined,
                        )
                        .map((booking: Booking) => ({
                            method: 'PUT',
                            endpoint: `/bookings/${booking.booking?.id}/pay`,
                            data: { paymentForm },
                        })),
                },
                success: (response: { responses?: unknown[] }) => {
                    setBatch([]);
                    if (response.responses) {
                        let existing = { ..._bookings };
                        response.responses.forEach((responseToSet: unknown) => {
                            const resp = responseToSet as {
                                booking?: Booking;
                                payment?: { parent?: { id?: string } };
                            };
                            if (resp.booking) {
                                existing = {
                                    ...existing,
                                    rows: existing.rows.map((row: any) =>
                                        resp.booking &&
                                        resp.booking.id === row.id
                                            ? resp.booking
                                            : row,
                                    ),
                                };
                            }
                            if (
                                resp.payment &&
                                idPayModal === null &&
                                resp.payment.parent
                            ) {
                                setIdPayModal(resp.payment.parent.id || null);
                            }
                        });
                        setBookings(existing);
                    }
                },
                error: () => setBatch([]),
            });
        }
    }

    function cancelBookings(rows: Booking[]) {
        if (rows.length > 0) {
            cxt?.api('POST', '/batch', {
                confirmation: true,
                params: {
                    calls: rows.map((booking: any) => ({
                        method: 'DELETE',
                        endpoint: `/bookings/${booking.id}`,
                    })),
                },
                success: () => {
                    setBookings({
                        count: (_bookings.count || 0) - rows.length,
                        rows: _bookings.rows.filter(
                            (booking: any) =>
                                rows.find(
                                    (row: any) => row.id === booking.id,
                                ) === undefined,
                        ),
                    });
                },
            });
        }
    }

    return (
        <>
            <SttTopFilteringControls
                extraFields={[
                    {
                        caption: t('Status'),
                        type: 'select',
                        name: 'status',
                        value: preset,
                        options: [
                            { caption: t('All'), value: 'ALL' },
                            {
                                caption: t('booking.status.in_progress'),
                                value: 'IN_PROGRESS',
                            },
                            {
                                caption: t('booking.status.succeeded'),
                                value: 'PROCESSED',
                            },
                            { caption: t('Error'), value: 'FAILED' },
                        ],
                    },
                    {
                        caption: t('Begin'),
                        name: 'ini',
                        type: 'date',
                        value: '',
                    },
                    {
                        caption: t('End'),
                        name: 'end',
                        type: 'date',
                        value: '',
                    },
                ]}
                onFilter={async (params: Record<string, unknown>) => {
                    // Pasamos el estado actual del select en los parámetros
                    await doFiltering({
                        ...params,
                        // Usamos el valor del select que viene en params si existe
                        status: params.status || preset,
                    });
                }}
                iconButtons={[
                    {
                        icon: <Refresh />,
                        onClick: handleRefresh,
                    },
                ]}
            />
            <SttTable
                autoload={false}
                onFetch={(params: Record<string, unknown>) => {
                    const cleanFilters = getCleanFilters();
                    const newPage = (params.page as number) || 1;
                    setCurrentPage(newPage);

                    // Combinamos los filtros activos con los parámetros de paginación
                    const fetchParams = {
                        ...cleanFilters, // Filtros activos
                        ...params, // Parámetros de paginación
                        page: newPage,
                        rows: (params.rows as number) || rowsPerPage,
                    };

                    // mantener status si está definido
                    if (preset && preset !== 'ALL' && !params.status) {
                        (fetchParams as any).status = preset;
                    }

                    onFetch(fetchParams);
                }}
                data={_bookings}
                onClickRow={(row: Booking) => {
                    if (row.booking?.error) {
                        let msg = row.booking.error;
                        if (msg && msg.includes('{{intervals}}')) {
                            const ini = row.booking?.ini;
                            const end = row.booking?.end;
                            const formatted =
                                ini && end
                                    ? `${new Date(ini).toLocaleString()} - ${new Date(end).toLocaleString()}`
                                    : `${ini || ''} ${end ? `- ${end}` : ''}`;
                            msg = msg.replace('{{intervals}}', formatted);
                        }
                        cxt?.showMessage('E', translatePlaceholders(msg, t));
                        return;
                    }
                    if (row.booking?.id) {
                        setViewBooking(row);
                    }
                }}
                selection={{
                    actions: [
                        {
                            caption: t('Collect'),
                            icon: <Euro />,
                            onClick: openCollectBookings,
                        },
                        {
                            caption: t('Delete'),
                            icon: <Close />,
                            onClick: cancelBookings,
                        },
                    ],
                }}
                selectRows={selectedRows}
                setSelectRows={(newSelectedRows: Booking[]) => {
                    const validRows = newSelectedRows.filter(
                        (row: any) =>
                            row.id &&
                            _bookings.rows?.some(
                                (r) => r.booking?.id === row.id,
                            ),
                    );
                    setSelectedRows(validRows);
                }}
                columns={[
                    {
                        align: 'left',
                        title: t('User'),
                        value: (row: Booking) => (
                            <SttChipUser
                                user={{ login: row.purse?.fullname || '' }}
                            />
                        ),
                    },
                    {
                        title: t('Date'),
                        type: 'period',
                        value: (row: Booking) => ({
                            ini: String(row.booking?.ini ?? row.ini ?? ''),
                            end: String(row.booking?.end ?? row.end ?? ''),
                        }),
                    },
                    {
                        title: t('Time'),
                        type: 'periodTime',
                        value: (row: Booking) => ({
                            ini: row.booking?.ini ?? row.ini ?? '',
                            end: row.booking?.end ?? row.end ?? '',
                        }),
                    },
                    {
                        title: t('Facility'),
                        type: 'facility',
                        field: 'facility',
                    },
                    {
                        title: t('Paid'),
                        align: 'right',
                        type: 'price',
                        value: (row: Booking) =>
                            getBookingScheduleBookingAmounts(row).paid,
                    },
                    {
                        title: t('Total'),
                        align: 'right',
                        type: 'price',
                        field: 'price',
                        value: (row: Booking) =>
                            getBookingScheduleBookingAmounts(row).total,
                    },
                    {
                        title: t('Pending'),
                        align: 'right',
                        type: 'price',
                        value: (row: Booking) =>
                            getBookingScheduleBookingAmounts(row).pending,
                    },
                    {
                        title: t('Status'),
                        field: 'status' as any,
                        value: (row: Booking) => renderStatus(row.status || ''),
                    },
                ]}
            />

            {viewBooking && viewBooking.booking?.id && (
                <BookingDialog
                    idBooking={Number(viewBooking.booking.id)}
                    onClose={() => setViewBooking(null)}
                    onUpdate={onFetch}
                />
            )}

            <ModalCollectBatch
                open={batch.length > 0}
                onClose={() => setBatch([])}
                bookings={batch}
                onCollect={(paymentForm: string) =>
                    collectBookings(paymentForm)
                }
            />

            {idPayModal && (
                <PaymentModal
                    idPayment={Number(idPayModal)}
                    onClose={() => setIdPayModal(null)}
                />
            )}
        </>
    );
}
